import {Component, Inject, OnInit, ViewEncapsulation} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {NotificationService} from '../../../../services/common/notification.service';
import {SharedService} from '../../../../services/common/shared.service';
import {SelectDialogComponent} from '../../../select/select-dialog/select-dialog.component';
import {GlobalCanDoStatementCategoryService} from '../../../../services/can-do-statement/global-can-do-statement-category.service';
import {DetailedCanDoStatementCategoryService} from '../../../../services/can-do-statement/detailed-can-do-statement-category.service';
import {CanDoStatementCategoryCreationDto} from '../../../../dtos/can-do-statement/category/can-do-statement-category-creation-dto';
import {References} from '../../../../dtos/references';

export interface NewCanDoStatementCategoryDialogData {
  id?: number;
  service: GlobalCanDoStatementCategoryService | DetailedCanDoStatementCategoryService;
}

@Component({
  selector: 'app-new-can-do-statement-category',
  templateUrl: './new-can-do-statement-category.component.html',
  styleUrls: ['./new-can-do-statement-category.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class NewCanDoStatementCategoryComponent implements OnInit {

  formGroup: UntypedFormGroup;
  selectedParentCategory: {id: number; name: string} | null = null;
  edit = false;
  id: number | null = null;
  // References are not editable through this dialog, but must be carried through
  // on edit so the update does not wipe the category's existing links.
  references: References;

  private service: GlobalCanDoStatementCategoryService | DetailedCanDoStatementCategoryService;
  private flavor: 'global' | 'detailed';

  constructor(
    private fb: UntypedFormBuilder,
    private dialogRef: MatDialogRef<NewCanDoStatementCategoryComponent>,
    private dialog: MatDialog,
    private notificationService: NotificationService,
    private sharedService: SharedService,
    @Inject(MAT_DIALOG_DATA) public data: NewCanDoStatementCategoryDialogData
  ) {
    this.service = data.service;
    this.flavor = data.service instanceof DetailedCanDoStatementCategoryService ? 'detailed' : 'global';
  }

  ngOnInit(): void {
    this.formGroup = this.fb.group({
      nameCtrl: ['', Validators.required],
      parentId: [{value: null, disabled: true}]
    });
    if (this.data?.id) {
      this.edit = true;
      this.id = this.data.id;
      this.service.getCategoryByID(this.id).subscribe({
        next: (category) => this.setForm(category),
        error: (error) => this.notificationService.openSnackBar(error)
      });
    }
  }

  setForm(category: any): void {
    this.references = this.sharedService.extractReferences(category)['Verweise'];
    this.formGroup.controls['nameCtrl'].setValue(category.name);
    if (category.parent) {
      this.selectedParentCategory = {id: category.parent.id, name: category.parent.name};
      this.formGroup.controls['parentId'].setValue(category.parent.id);
    }
  }

  openParentCategorySelect(): void {
    const selectionEntity = this.flavor === 'detailed' ? 'det_can_statements_category' : 'glob_can_statements_category';
    const columnPrefix = this.flavor === 'detailed' ? 'det-can-cat' : 'glob-can-cat';
    const dialogRef = this.dialog.open(SelectDialogComponent, {
      data: {
        selectedObject: this.selectedParentCategory,
        displayedColumns: ['single-select', columnPrefix + '-name', columnPrefix + '-parent'],
        selectionEntity: selectionEntity,
        dialogTitle: 'Übergeordnetes Thema auswählen',
        selectionMode: 'single-select',
        excludedId: this.id
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined) {
        if (result && result.length > 0 && result[0]) {
          this.selectedParentCategory = result[0];
          this.formGroup.controls['parentId'].setValue(result[0].id);
        } else {
          this.selectedParentCategory = null;
          this.formGroup.controls['parentId'].setValue(null);
        }
      }
    });
  }

  save(): void {
    if (this.formGroup.controls['nameCtrl'].invalid) {
      return;
    }
    const category = new CanDoStatementCategoryCreationDto(
      this.id,
      this.formGroup.controls['nameCtrl'].value,
      this.selectedParentCategory?.id ?? null,
      this.sharedService.getIds(this.references?.vocabCategories),
      this.sharedService.getIds(this.references?.vocabEntries),
      this.sharedService.getIds(this.references?.functionalGrammarCategories),
      this.sharedService.getIds(this.references?.functionalGrammarEntries),
      this.sharedService.getIds(this.references?.systemicGrammarCategories),
      this.sharedService.getIds(this.references?.systemicGrammarEntries),
      this.sharedService.getIds(this.references?.textPatterns),
      this.sharedService.getIds(this.references?.textTypes),
      this.sharedService.getIds(this.references?.detailedCanDoStatementCategories),
      this.sharedService.getIds(this.references?.detailedCanDoStatementEntries),
      this.sharedService.getIds(this.references?.globalCanDoStatementCategories),
      this.sharedService.getIds(this.references?.globalCanDoStatementEntries),
      this.sharedService.getIds(this.references?.verbalActionCategories),
      this.sharedService.getIds(this.references?.verbalActionEntries)
    );
    const request = this.edit
      ? this.service.updateCategory(category)
      : this.service.createCategory(category);

    request.subscribe({
      next: (result) => {
        this.notificationService.openSnackBar('Thema "' + result.name + '" erfolgreich ' + (this.edit ? 'aktualisiert' : 'erstellt'));
        this.dialogRef.close(true);
      },
      error: (error) => this.notificationService.openSnackBar(error)
    });
  }
}
